import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { lineReveal } from "./motion";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "../i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/31612212/pexels-photo-31612212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export const Hero = () => {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const scrollToNext = () => {
    const el = document.querySelector("#species");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const lines = t("hero.lines");

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen w-full items-end overflow-hidden pb-16 pt-32"
    >
      {/* Parallax image, offset right */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[58%]"
      >
        <img
          src={HERO_IMG}
          alt="Misty pine forest"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-bone/20" />
        <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-brand-bone to-transparent hidden md:block" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-16 bg-brand-timber" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
            {t("hero.kicker")}
          </span>
        </motion.div>

        <h1 key={lang} className="max-w-5xl font-serif text-[15vw] font-medium leading-[0.86] tracking-tight text-brand-blue sm:text-[13vw] md:text-[8.5vw]">
          {lines.map((line, i) => (
            <span key={line} className="mask-line">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md font-light text-base leading-relaxed text-brand-slate md:text-lg">
            {t("hero.sub")}
          </p>

          <button
            data-testid="hero-explore-btn"
            onClick={scrollToNext}
            className="group inline-flex items-center gap-3 self-start rounded-full border border-brand-blue px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-brand-blue transition-colors duration-300 hover:bg-brand-blue hover:text-brand-bone"
          >
            {t("hero.explore")}
            <ArrowDownRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
